import { EquipmentType as EquipmentTypeEnum } from '../../config/equipment-types';
import i18n from '../../config/i18n';

// Tipi equipment supportati per controllo temperatura (sincronizzato con backend)
type HaccpEquipmentType =
  | EquipmentTypeEnum.Frigorifero
  | EquipmentTypeEnum.Congelatore
  | EquipmentTypeEnum.MantenimentoCaldo;

// Costanti HACCP per range temperature (sincronizzato con backend).
// Un bound null significa "nessun limite su quel lato": il congelatore non ha
// un minimo, il mantenimento a caldo non ha un massimo. 65°C è un default
// modificabile dall'utente nel piano di autocontrollo, non un obbligo di legge.
export const HACCP_TEMPERATURE_RANGES: Record<
  HaccpEquipmentType,
  {
    min: number | null;
    max: number | null;
    label: string;
    description: string;
  }
> = {
  [EquipmentTypeEnum.Frigorifero]: {
    min: 0,
    max: 4,
    get label() {
      return i18n.t('temperature.fridge');
    },
    get description() {
      return i18n.t('temperature.idealTempFridge');
    },
  },
  [EquipmentTypeEnum.Congelatore]: {
    min: null,
    max: -18,
    get label() {
      return i18n.t('temperature.freezer');
    },
    get description() {
      return i18n.t('temperature.idealTempFreezer');
    },
  },
  [EquipmentTypeEnum.MantenimentoCaldo]: {
    min: 65,
    max: null,
    get label() {
      return i18n.t('temperature.hotHolding');
    },
    get description() {
      return i18n.t('temperature.idealTempHotHolding');
    },
  },
};

// Scostamento dal bound quando il range è aperto da un lato (congelatore,
// mantenimento a caldo): il default cade dentro il range, non sul limite.
const OPEN_RANGE_MARGIN_CELSIUS = 2;

export function isHaccpType(type: string): type is HaccpEquipmentType {
  return (
    type === EquipmentTypeEnum.Frigorifero ||
    type === EquipmentTypeEnum.Congelatore ||
    type === EquipmentTypeEnum.MantenimentoCaldo
  );
}

/**
 * Temperatura di default conforme, derivata dal range effettivo
 * dell'attrezzatura: il range configurato dall'utente vince sul range HACCP del
 * tipo, esattamente come in TemperatureCheckService.validateTemperature().
 * Prefillare il default del tipo su una cella con range custom produrrebbe un
 * valore fuori range già al primo render.
 */
export function getDefaultTemperature(
  equipmentType: string,
  rangeOverride?: { min?: number | null; max?: number | null },
): number | null {
  if (!isHaccpType(equipmentType)) {
    return null;
  }
  const baseRange = HACCP_TEMPERATURE_RANGES[equipmentType];
  const min = rangeOverride?.min ?? baseRange.min;
  const max = rangeOverride?.max ?? baseRange.max;

  if (min !== null && max !== null) {
    return Math.round(((min + max) / 2) * 10) / 10;
  }
  if (max !== null) {
    return max - OPEN_RANGE_MARGIN_CELSIUS;
  }
  if (min !== null) {
    return min + OPEN_RANGE_MARGIN_CELSIUS;
  }
  return null;
}

// Type alias per compatibilità con il codice esistente
export type EquipmentType = HaccpEquipmentType;

export interface TemperatureCheck {
  id: string;
  equipmentId: string;
  equipmentName: string;
  equipmentType: EquipmentType;
  temperature: number;
  isCompliant: boolean;
  recordedAt: string;
  operatorName?: string;
  performedBy?: string;
  performerName?: string;
  signatureImage?: string;
  /**
   * Register-fact provenance markers (mirrors backend
   * `EquipmentCheckRecord.pendingSignature`/`ingestedVia`, see
   * `backend/src/equipment/domain/register-fact.ts`). Needed to tell a
   * validated human registration apart from an IoT proposal or an
   * unvalidated reading when counting adempimenti — see
   * `pages/self-check/temperature/utils/equipment-stats.ts`'s
   * `isRegisterFact`.
   */
  pendingSignature: boolean;
  ingestedVia: string | null;
}

export interface CreateTemperatureCheckRequest {
  equipmentId: string;
  equipmentName: string;
  equipmentType: EquipmentType;
  temperature: number;
  operatorName?: string;
}

export interface TemperatureValidationResult {
  isCompliant: boolean;
  temperature: number;
  equipmentType: EquipmentType;
  range: {
    min: number | null;
    max: number | null;
    label: string;
    description: string;
  };
  message: string;
}

type TemperatureDayStatus =
  | 'complete'
  | 'partial'
  | 'nonCompliant'
  | 'missing'
  | 'notRequired'
  | 'future';

interface TemperatureCalendarStats {
  complete: number;
  partial: number;
  nonCompliant: number;
  missing: number;
  notRequired: number;
}

export interface TemperatureCalendarResponse {
  calendar: Record<string, TemperatureDayStatus>;
  stats: TemperatureCalendarStats;
}

/**
 * The register's obligation state for a piece of equipment today — replaces
 * the old `isComplete` boolean, which counted any non-pending record without
 * looking at who wrote it (the Sim-Frigo bug: a machine-written midnight
 * alarm closed the day in the operator's place).
 *
 * - `surveilled` — probe active within budget: no obligation, no input field
 * - `due` — budget exhausted or an open suggestion: a person is needed
 * - `fulfilled` — a signed human registration exists for today
 * - `unregistered` — day closed with the obligation never satisfied (only
 *   ever returned for past days, never by the today-scoped status endpoints)
 */
export type TemperatureDayState = 'surveilled' | 'due' | 'fulfilled' | 'unregistered';

interface EquipmentStatus {
  id: string;
  name: string;
  type: string;
  checkFrequency: number;
  minTemperature?: number | null;
  maxTemperature?: number | null;
  todaySignedCount: number;
  dayState: TemperatureDayState;
  /** Cumulative minutes the probe has been silent today; 0 for sensorless equipment. */
  blindMinutes: number;
  /** Minutes of silence tolerated today before the register falls back to a human obligation. */
  blindMinutesBudget: number;
  /** Technical IoT threshold/risk alert; not a HACCP record. */
  technicalAlertActive: boolean;
  /** Cause of the currently open gap, if any; null when no gap is open right now. */
  interruptionCause: 'gateway-offline' | 'sensor-silent' | null;
  /** Cause of the technical alert, if active; drives the banner copy — see `resolveTechnicalAlertCopy`. */
  technicalAlertCause: 'out-of-range' | 'cumulative-risk' | 'recovered' | null;
  /** Temperature that triggered the alert episode, persisted at trigger time; not the live reading. */
  technicalAlertTemperature: number | null;
  /** Preset threshold in effect for a cumulative-risk alert, if any. */
  technicalAlertThresholdMax: number | null;
  lastTemperature?: number;
}

export interface TemperatureCheckStatus {
  equipment: EquipmentStatus[];
  overallComplete: boolean;
  completedCount: number;
  totalCount: number;
}

export interface SignEquipmentBatchRequest {
  equipment: Array<{
    equipmentId: string;
    temperature: number;
  }>;
  signatureImage: string;
}

export interface SignEquipmentBatchResult {
  records: TemperatureCheck[];
  /** Equipment IDs skipped because already at checkFrequency for today. */
  skippedEquipment: string[];
}

interface EquipmentWithTodayStatus {
  id: string;
  tenantId: string;
  name: string;
  type: string;
  checkFrequency: number;
  checkFrequencyUnit: string;
  minTemperature?: number | null;
  maxTemperature?: number | null;
  createdAt: string;
  updatedAt: string;
  dayState: TemperatureDayState;
  /** Cumulative minutes the probe has been silent today; 0 for sensorless equipment. */
  blindMinutes: number;
  /** Minutes of silence tolerated today before the register falls back to a human obligation. */
  blindMinutesBudget: number;
  /** Technical IoT threshold/risk alert; not a HACCP record. */
  technicalAlertActive: boolean;
  /** Cause of the currently open gap, if any; null when no gap is open right now. */
  interruptionCause: 'gateway-offline' | 'sensor-silent' | null;
  /** Cause of the technical alert, if active; drives the banner copy — see `resolveTechnicalAlertCopy`. */
  technicalAlertCause: 'out-of-range' | 'cumulative-risk' | 'recovered' | null;
  /** Temperature that triggered the alert episode, persisted at trigger time; not the live reading. */
  technicalAlertTemperature: number | null;
  /** Preset threshold in effect for a cumulative-risk alert, if any. */
  technicalAlertThresholdMax: number | null;
  lastTemperature?: number;
  /**
   * Most recent recorded temperature regardless of day (manual entries only).
   * Prefill suggestion for the first entry of the day, when `lastTemperature`
   * is by definition absent. Never used to decide compliance or completion.
   */
  lastKnownTemperature?: number;
  todaySignedCount: number;
  /** Id of today's most recent temperature record for this equipment — the correction affordance targets it. */
  todayLastRecordId?: string;
  /**
   * Today's cumulative-risk NC (iot-auto-nc) for this equipment, if any — a
   * proposed non-conformity, not a confirmed one. `reviewOutcome` distinguishes
   * an open suggestion (`null`) from one a human has already confirmed or
   * dismissed with a thermometer reading; see `TodayNonCompliance.reviewOutcome`
   * for why `ingestedVia` alone (always `'iot-auto-nc'` here) cannot tell.
   */
  iotAutoNcRecord?: {
    id: string;
    recordedAt: string;
    degMin: number;
    reviewOutcome: 'confirmed' | 'dismissed' | null;
  } | null;
  /** Bound sensor's risk snapshot, if any. Drives EquipmentRiskBadge on the card. */
  iotSensor?: {
    foodPresetId: string | null;
    riskDegMin: number;
    riskLevel: 'high' | 'medium' | 'ok' | null;
  } | null;
  /** Today's most recent out-of-range reading — the reason the card is red. */
  todayNonCompliance?: TodayNonCompliance | null;
}

/**
 * Today's out-of-range reading, as recorded on the register. Distinct from both
 * the live sensor value and the cumulative-risk gauge: those describe now and
 * the 24h heat budget, this one is the entry that already turned the card red.
 */
export interface TodayNonCompliance {
  id: string;
  recordedAt: string;
  temperature: number;
  /** `null` for manual entries, `iot-alert` for the automatic breach record. */
  ingestedVia: string | null;
  /**
   * The review endpoint's verdict, when this is a suggestion already looked
   * at — `null` while still open, or for a manual entry. See
   * `isOpenIotSuggestion` for why `ingestedVia` alone cannot tell "still
   * needs a human" apart from "already handled".
   */
  reviewOutcome: 'confirmed' | 'dismissed' | null;
}

export interface InactiveEquipment {
  id: string;
  name: string;
  type: string;
  reason: string | null;
}

export interface EquipmentStatusList {
  equipment: EquipmentWithTodayStatus[];
  inactiveEquipment: InactiveEquipment[];
  overallComplete: boolean;
  completedCount: number;
  totalCount: number;
}
